import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import { LoadingButton } from '@mui/lab';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { addPharmacyShop, updatePharmacyShop } from '../../apis/pharmacyShopSlice';
import SearchClinic from '../../components/Autocomplete/SearchClinic';
import { Button, TextField } from '@mui/material';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

export default function PharmacyDialog(props) {
  const { open, setOpen, editData, operationMode, setOperationMode, callApi } = props;
  const dispatch = useDispatch();

  const [shopData, setShopData] = useState({
    shopName: '',
    ownerName: '',
    email: '',
    phone: '',
    address: '',
    clinicId: '',
  });
  const [errors, setErrors] = useState({
    email: '',
  });

  const { loggedIn } = useSelector((state) => state.authData || {});
  const { loading } = useSelector((state) => state.pharmacyShopData || {});

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  React.useEffect(() => {
    if (editData && Object.keys(editData || {}).length > 0 && operationMode === 'Edit') {
      setShopData({
        ...editData,
        clinicId: editData.clinicId?._id || editData.clinicId || '',
      });
    } else {
      setShopData({ shopName: '', ownerName: '', email: '', phone: '', address: '', clinicId: '' });
      setOperationMode('Add');
    }
  }, [editData, operationMode]);

  React.useEffect(() => {
    if (open && operationMode === 'Add' && loggedIn?.clinicId) {
      setShopData((prev) => ({ ...prev, clinicId: loggedIn.clinicId }));
    }
  }, [open, operationMode, loggedIn?.clinicId]);

  const handleClose = () => {
    setOpen(false);
    setShopData({ shopName: '', ownerName: '', email: '', phone: '', address: '', clinicId: '' });
     setErrors({ email: '' });
    setOperationMode('Add');
  };

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    let errorMsg = '';

    if (name === 'email') {
      if (newValue && !emailRegex.test(newValue)) {
        errorMsg = 'Enter valid email';
      }
    }

    setErrors((prev) => ({ ...prev, [name]: errorMsg }));

    setShopData({ ...shopData, [name]: value });
  };

  const handleSubmit = async () => {
    if (!shopData.clinicId) return toast.error('Please select clinic');
    if (!shopData.shopName) return toast.error('Please enter shop name');
    if (!shopData.email) return toast.error('Please enter email');

    if (shopData.email && !emailRegex.test(shopData.email)) {
      return toast.error('Enter valid email');
    }

    const response = await dispatch(
      operationMode == 'Add'
        ? addPharmacyShop(shopData)
        : updatePharmacyShop({ ...shopData, id: shopData._id }),
    );

    if (!response.payload?.data) {
      return toast.error(response.payload?.message || 'Error');
    } else {
      handleClose();
      toast.success(response.payload?.message);
    }

    callApi();
  };

  const disabled = !shopData.clinicId || !shopData.shopName.trim() || !shopData.email.trim() || !!errors.email;

  return (
    <React.Fragment>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, border: '1px solid var(--color-border-soft)' } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: 'var(--button-text)',
            borderBottom: '1px solid var(--color-border-soft)',
            background: 'var(--gradient-primary)',
            py: 1.15,
          }}
        >
          {operationMode} Pharmacy Shop
        </DialogTitle>
        <DialogContent
          className="modalContent"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            background: '#fff',
            pt: 1.4,
            '& .MuiInputBase-root': { fontSize: '0.84rem' },
            '& .MuiInputLabel-root': { fontSize: '0.8rem' },
          }}
        >
          <div style={{ marginTop: '5px' }}>
            <SearchClinic
              open={open}
              setData={setShopData}
              data={shopData}
              name="clinicId"
              label="Clinic"
              size="small"
            />
          </div>
          <div>
            <TextField
              label="Shop Name"
              type="text"
              size="small"
              fullWidth
              name="shopName"
              value={shopData?.shopName}
              onChange={handleOnChange}
              required
            />
          </div>
          <div>
            <TextField
              label="Owner Name"
              type="text"
              size="small"
              fullWidth
              name="ownerName"
              value={shopData?.ownerName}
              onChange={handleOnChange}
            />
          </div>
          <div>
            {/* <TextField label="Email" type="email" size="small" fullWidth name="email" value={shopData?.email} onChange={handleOnChange} required /> */}
            <TextField
              label="Email"
              type="email"
              size="small"
              fullWidth
              name="email"
              value={shopData?.email}
              onChange={handleOnChange}
              required
              error={!!errors.email}
              helperText={errors.email}
            />
          </div>
          <div>
            <TextField
              label="Phone"
              type="text"
              size="small"
              fullWidth
              name="phone"
              value={shopData?.phone}
              onChange={handleOnChange}
              inputProps={{ maxLength: 10 }}
            />
          </div>
          <div>
            <TextField
              label="Address"
              type="text"
              size="small"
              fullWidth
              name="address"
              value={shopData?.address}
              onChange={handleOnChange}
            />
          </div>
        </DialogContent>
        <DialogActions className="modalFooter" sx={{ borderTop: '1px solid var(--color-border-soft)', p: 1.25 }}>
          <Button
            onClick={handleClose}
            sx={{ textTransform: 'none', borderRadius: '8px', border: '1px solid var(--color-border-soft)', color: 'var(--color-heading)' }}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={loading}
            variant="contained"
            onClick={handleSubmit}
            className={disabled ? 'dialogDisableBtn' : 'dialogSubmitBtn'}
            disabled={disabled}
            sx={{ textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}
          >
            Submit
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
